import { decode as decodeBase64 } from 'base-64';
import isEmpty from 'lodash-es/isEmpty';
import { Resource } from '../Resource';
import * as methods from '../methods';
import * as transformers from '../transformers';
import me from './me';
import networkOrgs from './networkOrgs';
import orgs from './orgs';
import { sendableFromRaw } from './sendable';
const rootPath = 'invitations';
/**
 * @zod
 * An invitation is asking someone to join (e.g a network invites an org)
 * A request is someone asking to join (e.g an org requested to become an adherent farm inside a network)
 */
export var RequestType;
(function (RequestType) {
    /** A user was invited to become a member of an organization */
    RequestType[RequestType["orgMembershipInvitation"] = 0] = "orgMembershipInvitation";
    /** @deprecated The owner of an org invited a member of the org to become owner */
    RequestType[RequestType["orgOwnershipTransfer"] = 1] = "orgOwnershipTransfer";
    /** A network-less organization requested to join a network */
    RequestType[RequestType["adherentFarmRequest"] = 2] = "adherentFarmRequest";
    /** A network invited an org to become an adherent farm */
    RequestType[RequestType["adherentFarmInvitation"] = 3] = "adherentFarmInvitation";
    /** A network-less organization requested to join a network (with a trial period) */
    RequestType[RequestType["adherentFarmRequestWithTrial"] = 4] = "adherentFarmRequestWithTrial";
    /** A network invited an org to become an adherent farm (with a trial period) */
    RequestType[RequestType["adherentFarmInvitationWithTrial"] = 5] = "adherentFarmInvitationWithTrial";
    /** An adherent farm requested to purchase an option  */
    RequestType[RequestType["adherentFarmOptionPurchaseRequest"] = 6] = "adherentFarmOptionPurchaseRequest";
    /**
     * Invitation send to an organization without an account
     * A pre-configured account is created and the invitation is accepted automatically (by this new account)
     */
    RequestType[RequestType["autoAcceptedAdherentFarmInvitation"] = 7] = "autoAcceptedAdherentFarmInvitation";
    /**
     * Invitation send to a user (member) without an account
     * A pre-configured account is created and the invitation is accepted automatically (by this new account)
     */
    RequestType[RequestType["autoAcceptedMemberInvitation"] = 8] = "autoAcceptedMemberInvitation";
})(RequestType || (RequestType = {}));
/** This does NOT expect a regular invitation but an email invitation */
export const fromRawToParsedEmailInvitation = ({ invitation: id, email, user, type, network_name: networkName, organization_name: orgName, device_count: devicesCount, phone, country, first_name: firstName, last_name: lastName, role }) => ({
    id,
    email,
    /**
     * When the invitation is creating a user (preset account),
     * We should handle it as unknown in the front, because the user still needs to go through the registering process
     * This happens in both members and adherent farm invitations
     */
    isKnownUser: user === 1
        ? type !== RequestType.autoAcceptedAdherentFarmInvitation &&
            type !== RequestType.autoAcceptedMemberInvitation
        : false,
    user,
    type,
    networkName,
    orgName,
    devicesCount,
    phone,
    firstName,
    lastName,
    role,
    country
});
export const parseEmailInvitation = (invitationBase64) => fromRawToParsedEmailInvitation(JSON.parse(decodeBase64(invitationBase64)));
const fromRaw = (isSentToMe) => (raw) => {
    return {
        acceptedAt: raw.accepted_at,
        acceptUrl: raw.accept_url,
        canceledAt: raw.canceled_at,
        chargedToNetwork: raw.charged_to_network,
        createdAt: raw.created_at,
        declinedAt: raw.declined_at,
        id: raw.id,
        isSentToMe: isSentToMe || false,
        metadata: raw.metadata
            ? {
                billingStatus: raw.metadata.licence,
                firstName: raw.metadata.first_name,
                lastName: raw.metadata.last_name,
                membershipCode: raw.metadata.membership_code,
                organizationName: raw.metadata.organization_name,
                phone: raw.metadata.phone
            }
            : undefined,
        optionCode: raw?.metadata?.option,
        organizationMemberCount: raw.member_count,
        recipientEmail: raw.recipient_email,
        recipientName: raw.recipient_name,
        recipientOrgName: raw.recipient_org_name,
        role: raw.role,
        senderOrganizationId: raw.from_organization,
        senderOrganizationName: raw.from_organization_name,
        status: raw.status,
        type: raw.type,
        ...sendableFromRaw(raw)
    };
};
const invitations = Resource((requesterBuilder, config) => {
    const getSent = (query) => requesterBuilder({
        path: `${rootPath}/sent`,
        requestCtx: {
            transform: transformers.paginatedResponse(fromRaw(false)),
            params: {
                query: {
                    offset: query?.offset,
                    limit: query?.limit,
                    from_organization: query?.fromOrganizationId,
                    type: query?.type,
                    role: query?.roles,
                    q: query?.q,
                    sort_by: query?.sortBy,
                    sort_order: query?.sortOrder,
                    status: query?.status
                }
            }
        }
    });
    const getReceived = (query) => requesterBuilder({
        path: `me/invitations`,
        requestCtx: {
            transform: transformers.paginatedResponse(fromRaw(true)),
            params: {
                query: {
                    offset: query?.offset,
                    limit: query?.limit,
                    q: query?.q,
                    status: query?.status,
                    sort_by: query?.sortBy,
                    sort_order: query?.sortOrder
                }
            }
        }
    });
    const getReceivedPerNetwork = (query) => requesterBuilder({
        path: `${rootPath}/received`,
        requestCtx: {
            transform: transformers.paginatedResponse(fromRaw(true)),
            params: {
                query: {
                    offset: query?.offset,
                    limit: query?.limit,
                    to_organization: query?.networkId,
                    status: query?.status,
                    type: query?.type,
                    q: query?.q,
                    option: query?.option,
                    sort_by: query?.sortBy,
                    sort_order: query?.sortOrder
                }
            }
        }
    });
    const getSentAsCSV = (query) => requesterBuilder({
        path: `${rootPath}/sent/export`,
        requestCtx: {
            method: 'GET',
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            parse: (x) => x.blob(),
            transform: (blob) => blob,
            params: {
                query: {
                    organization: query.organizationId,
                    type: query?.type,
                    with_role: query?.withRole
                }
            }
        }
    });
    const getAllSent = methods.getAllPages({ getMany: getSent });
    const getPageSent = methods.getPage({ getMany: getSent });
    const meInvalidationKey = me(requesterBuilder, config).getMyInfo().invalidationKey;
    const sentInvitationsInvalidationKeys = [meInvalidationKey, getSent().invalidationKey];
    const receivedPerNetworkInvitationsInvalidationKeys = [
        meInvalidationKey,
        getReceivedPerNetwork().invalidationKey
    ];
    const receivedInvitationsInvalidationKeys = [meInvalidationKey, getReceived().invalidationKey];
    return {
        deletion: methods.deletion({
            rootPath: `${rootPath}/archiving`,
            requesterBuilder,
            buildKeysToInvalidate: () => sentInvitationsInvalidationKeys,
            patternToInvalidate: 'invitations'
        }),
        sent: {
            get: methods.get({ requesterBuilder, rootPath: `${rootPath}/sent`, fromRaw: fromRaw(false) }),
            getMany: getSent,
            getAll: getAllSent,
            getPage: getPageSent,
            cancel: (id) => requesterBuilder({
                path: `${rootPath}/${id}/cancel`,
                requestCtx: {
                    host: config.getCtx().hosts.tiles,
                    method: 'POST',
                    parse: async (x) => x
                },
                keysToInvalidate: sentInvitationsInvalidationKeys,
                patternToInvalidate: 'invitations/sent'
            }),
            download: getSentAsCSV
        },
        received: {
            get: methods.get({
                requesterBuilder,
                rootPath: `me/invitations`,
                fromRaw: fromRaw(true)
            }),
            getMany: getReceived,
            getAll: methods.getAllPages({ getMany: getReceived }),
            getPage: methods.getPage({ getMany: getReceived }),
            decline: (id) => requesterBuilder({
                path: `me/invitations/${id}/decline`,
                requestCtx: {
                    method: 'POST',
                    parse: async (x) => x
                },
                keysToInvalidate: receivedInvitationsInvalidationKeys,
                patternToInvalidate: 'me/invitations'
            }),
            accept: (id) => methods.create({
                requesterBuilder,
                rootPath: `me/invitations/${id}/accept`,
                buildKeysToInvalidate: () => [
                    ...receivedInvitationsInvalidationKeys,
                    orgs(requesterBuilder, config).getMany().invalidationKey
                ],
                fromRaw: async (x) => x,
                toRaw: ({ organizationId }) => ({
                    to_organization: organizationId
                }),
                patternToInvalidate: 'me/invitations'
            })()
        },
        receivedPerNetwork: {
            get: methods.get({
                requesterBuilder,
                rootPath: `${rootPath}/received`,
                fromRaw: fromRaw(false)
            }),
            getMany: getReceivedPerNetwork,
            getAll: methods.getAllPages({ getMany: getReceivedPerNetwork }),
            getPage: methods.getPage({ getMany: getReceivedPerNetwork }),
            decline: (id) => requesterBuilder({
                path: `${rootPath}/received/${id}/decline`,
                requestCtx: {
                    method: 'POST',
                    parse: async (x) => x
                },
                keysToInvalidate: receivedPerNetworkInvitationsInvalidationKeys,
                patternToInvalidate: 'invitations/received'
            }),
            accept: (id) => requesterBuilder({
                path: `${rootPath}/received/${id}/accept`,
                requestCtx: {
                    method: 'POST',
                    parse: async (x) => x
                },
                keysToInvalidate: [
                    ...receivedPerNetworkInvitationsInvalidationKeys,
                    networkOrgs(requesterBuilder, config).getMany().invalidationKey
                ],
                patternToInvalidate: 'invitations/received'
            })
        },
        invite: {
            toJoinNetwork: ({ networkId }) => requesterBuilder({
                path: `networks/${networkId}/invite_organization`,
                requestCtx: {
                    method: 'POST',
                    transform: fromRaw(false),
                    toRaw: (p) => 'billingStatus' in p
                        ? {
                            email: p.email,
                            licence: p.billingStatus,
                            trial: p.trial,
                            // Optional fields, they should not be sent if they are empty
                            ...(isEmpty(p.membershipCode)
                                ? {}
                                : {
                                    membership_code: p.membershipCode
                                }),
                            ...(isEmpty(p.firstName)
                                ? {}
                                : {
                                    first_name: p.firstName
                                }),
                            ...(isEmpty(p.lastName)
                                ? {}
                                : {
                                    last_name: p.lastName
                                }),
                            ...(isEmpty(p.organizationName)
                                ? {}
                                : {
                                    organization_name: p.organizationName
                                }),
                            // We would have a 400 if we send an incomplete phone
                            ...(isEmpty(p.phone?.number?.trim()) ? {} : { phone: p.phone })
                        }
                        : { email: p.email }
                },
                keysToInvalidate: sentInvitationsInvalidationKeys
            }),
            toJoinOrganization: ({ organizationId }) => requesterBuilder({
                path: `organizations/${organizationId}/membership_request`,
                requestCtx: {
                    method: 'POST',
                    transform: fromRaw(false)
                },
                keysToInvalidate: sentInvitationsInvalidationKeys
            })
        },
        beg: {
            networkMembership: (networkId) => requesterBuilder({
                path: `networks/${networkId}/request_network_membership`,
                requestCtx: {
                    method: 'POST',
                    parse: async (x) => x,
                    toRaw: ({ organizationId, trial }) => ({
                        from_organization: organizationId,
                        trial
                    })
                },
                keysToInvalidate: sentInvitationsInvalidationKeys
            })
        }
    };
});
export default invitations;
